import React, { createContext, useContext, useRef, useEffect, useCallback, useState } from 'react';

export interface OrderStatusEvent {
  type: 'order_status';
  order_id: number;
  status: string;
  message: string;
  updated_at: string;
}

export interface NewOrderEvent {
  type: 'new_order';
  order_id: number;
  [key: string]: unknown;
}

export type OrderSocketEvent = OrderStatusEvent | NewOrderEvent;

type OrderStatusListener = (event: OrderStatusEvent) => void;
type NewOrderListener = (event: NewOrderEvent) => void;

interface OrderSocketContextValue {
  subscribe: (listeners: {
    onOrderStatus?: OrderStatusListener;
    onNewOrder?: NewOrderListener;
  }) => () => void;
  status: 'connecting' | 'open' | 'closed';
}

const OrderSocketContext = createContext<OrderSocketContextValue | null>(null);

const PING_INTERVAL_MS = 25000;
const RECONNECT_DELAY_MS = 3000;
const WS_HOST = 'api.homecookt.com';
const WS_PATH = '/ws/orders';

export const OrderSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const socketRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldConnectRef = useRef(true);

  const statusListenersRef = useRef<Set<OrderStatusListener>>(new Set());
  const newOrderListenersRef = useRef<Set<NewOrderListener>>(new Set());

  const [status, setStatus] = useState<'connecting' | 'open' | 'closed'>('closed');

  const clearTimers = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!shouldConnectRef.current) return;

    const token = localStorage.getItem('authToken');
    if (!token) {
      setStatus('closed');
      return;
    }

    if (socketRef.current) {
      socketRef.current.onopen = null;
      socketRef.current.onmessage = null;
      socketRef.current.onerror = null;
      socketRef.current.onclose = null;
      socketRef.current.close();
      socketRef.current = null;
    }
    clearTimers();

    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${wsProtocol}://${WS_HOST}${WS_PATH}?token=${encodeURIComponent(token)}`;
    setStatus('connecting');

    const socket = new WebSocket(url);
    socketRef.current = socket;

    socket.onopen = () => {
      setStatus('open');
      pingIntervalRef.current = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'ping' }));
        }
      }, PING_INTERVAL_MS);
    };

    socket.onmessage = (event) => {
      let data: OrderSocketEvent;
      try {
        data = JSON.parse(event.data) as OrderSocketEvent;
      } catch {
        return;
      }
      if (data.type === 'order_status') {
        statusListenersRef.current.forEach((fn) => fn(data as OrderStatusEvent));
      } else if (data.type === 'new_order') {
        newOrderListenersRef.current.forEach((fn) => fn(data as NewOrderEvent));
      }
    };

    socket.onclose = () => {
      setStatus('closed');
      clearTimers();
      socketRef.current = null;
      if (!shouldConnectRef.current) return;
      reconnectTimerRef.current = setTimeout(() => {
        connect();
      }, RECONNECT_DELAY_MS);
    };

    socket.onerror = () => {
      // error is followed by close event which triggers reconnect
    };
  }, [clearTimers]);

  useEffect(() => {
    shouldConnectRef.current = true;
    connect();

    return () => {
      shouldConnectRef.current = false;
      clearTimers();
      if (socketRef.current) {
        socketRef.current.onclose = null;
        socketRef.current.close();
        socketRef.current = null;
      }
      setStatus('closed');
    };
  }, [connect, clearTimers]);

  const subscribe = useCallback(
    (listeners: {
      onOrderStatus?: OrderStatusListener;
      onNewOrder?: NewOrderListener;
    }) => {
      if (listeners.onOrderStatus) statusListenersRef.current.add(listeners.onOrderStatus);
      if (listeners.onNewOrder) newOrderListenersRef.current.add(listeners.onNewOrder);

      return () => {
        if (listeners.onOrderStatus) statusListenersRef.current.delete(listeners.onOrderStatus);
        if (listeners.onNewOrder) newOrderListenersRef.current.delete(listeners.onNewOrder);
      };
    },
    [],
  );

  return (
    <OrderSocketContext.Provider value={{ subscribe, status }}>
      {children}
    </OrderSocketContext.Provider>
  );
};

export const useOrderSocketContext = (): OrderSocketContextValue => {
  const ctx = useContext(OrderSocketContext);
  if (!ctx) {
    throw new Error('useOrderSocketContext must be used within an OrderSocketProvider');
  }
  return ctx;
};
